import React, { FC } from 'react';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import type { ConsentValues } from '../types/flow';
import { consentItems } from '../data/constants';
import ConsentCard from '../../assessment-portal/components/consent-card';
import NavRow from '../../assessment-portal/components/navigation-row';

interface ConsentPageProps {
  defaultValues: ConsentValues;
  onBack: () => void;
  onNext: (_values: ConsentValues) => void;
}

const ConsentPage: FC<ConsentPageProps> = ({
  defaultValues,
  onBack,
  onNext,
}) => {
  const methods = useForm<ConsentValues>({
    defaultValues: defaultValues || Object.fromEntries(consentItems.map((i) => [i.key, false])),
  });

  const { watch, setValue, handleSubmit } = methods;
  const values = watch();
  const accepted = _.size(_.pickBy(values));
  const allConsented = accepted === consentItems.length;

  const handleNext = handleSubmit((data) => onNext(data));

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-none md:rounded-2xl shadow-sm border border-slate-200 p-4 md:p-8">
      <h2 className="text-xl font-bold mb-1 text-brand-dark">Consents &amp; Agreements</h2>
      <p className="text-slate-500 text-sm mb-4">Please read and accept each item to continue.</p>

      <div
        className="hidden md:flex items-center gap-3 mb-5 p-3 border rounded-2lg bg-brand bg-opacity-5 border-brand border-opacity-10"
      >
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 bg-brand"
            style={{ width: `${(accepted / consentItems.length) * 100}%` }}
          />
        </div>
        <span className="text-xs font-bold flex-shrink-0 text-brand">
          {accepted}
          /
          {consentItems.length}
          {' '}
          accepted
        </span>
      </div>

      <div className="space-y-3">
        {consentItems.map((item) => (
          <ConsentCard
            key={item.key}
            item={item}
            accepted={!!values[item.key]}
            onToggle={() => setValue(item.key, !values[item.key])}
          />
        ))}
      </div>

      {allConsented && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-3 text-center text-green-700 text-sm font-semibold">
          ✅ All consents accepted — you&apos;re ready to continue
        </div>
      )}

      <NavRow
        onBack={onBack}
        onNext={handleNext}
        nextDisabled={!allConsented}
        nextLabel="Continue to Your Details →"
        backLabel="← Welcome"
        footerSlot={(
          <div className="flex items-center justify-between pb-3">
            <b className="text-sm font-semibold text-slate-700">
              {accepted}
              {' '}
              of
              {' '}
              {consentItems.length}
              {' '}
              accepted
            </b>
            <div className="flex-1 h-1 bg-slate-200 rounded-full ml-3 overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-500"
                style={{ width: `${(accepted / consentItems.length) * 100}%` }}
              />
            </div>
          </div>
        )}
        hint={allConsented ? undefined : 'Accept all four to continue'}
      />
    </div>
  );
};

export default ConsentPage;
